import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowRight, User, Heart, Bookmark, Search, Clock } from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ArtworksPage = () => {
  const [artworks, setArtworks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();

  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        // 获取所有用户发布的作品（包括pending状态），按时间排序
        const res = await axios.get(`${API}/artworks?include_user_artworks=true`);
        setArtworks(res.data);
      } catch (err) {
        console.error('Failed to fetch artworks:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchArtworks();
  }, []);

  // Get localized field
  const getLocalizedField = (item, field) => {
    if (!item) return '';
    const lang = i18n.language;
    if (lang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    if (lang === 'ru' && item[`${field}_ru`]) return item[`${field}_ru`];
    return item[field] || '';
  };

  // 点赞
  const handleLike = async (e, artworkId) => {
    e.stopPropagation();
    if (!user) {
      toast.error(t('artCanvas.loginFirst'));
      return;
    }
    try {
      const res = await axios.post(`${API}/artworks/${artworkId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtworks(prev => prev.map(artwork => {
        if (artwork.id === artworkId) {
          return {
            ...artwork,
            is_liked: res.data.liked,
            likes_count: res.data.liked 
              ? (artwork.likes_count || 0) + 1 
              : Math.max((artwork.likes_count || 0) - 1, 0)
          };
        }
        return artwork;
      }));
      toast.success(res.data.liked ? t('artCanvas.liked') : t('artCanvas.unliked'));
    } catch (err) {
      console.error('Like failed:', err);
      toast.error(t('artCanvas.operationFailed'));
    }
  };

  // 收藏
  const handleSave = async (e, artworkId) => {
    e.stopPropagation();
    if (!user) {
      toast.error(t('artCanvas.loginFirst'));
      return;
    }
    try {
      const res = await axios.post(`${API}/artworks/${artworkId}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtworks(prev => prev.map(artwork => {
        if (artwork.id === artworkId) {
          return {
            ...artwork,
            is_saved: res.data.saved,
            saves_count: res.data.saved 
              ? (artwork.saves_count || 0) + 1 
              : Math.max((artwork.saves_count || 0) - 1, 0)
          };
        }
        return artwork;
      }));
      toast.success(res.data.saved ? t('artCanvas.saved') : t('artCanvas.unsaved'));
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(t('artCanvas.operationFailed'));
    }
  };

  // 过滤作品
  const filteredArtworks = artworks.filter(artwork => {
    if (!searchTerm) return true;
    return artwork.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artwork.artist?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artwork.creator_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // 格式化时间
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return t('common.today', '今天');
    if (days === 1) return t('common.yesterday', '昨天');
    if (days < 7) return t('common.daysAgo', '{{days}}天前', { days });
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50 pt-16">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-light mb-4">
            {t('artworks.allArtworks', '全部作品')}
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
            {t('artworks.allArtworksDesc', '发现来自全球艺术家的精彩创作')}
          </p>
          
          {/* 搜索框 */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500" />
            <Input
              placeholder={t('artworks.searchPlaceholder', '搜索作品或艺术家...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 pr-4 py-3 rounded-full bg-amber-50/10 border-white/20 text-white placeholder:text-white/50 focus:bg-amber-50/20"
            />
          </div>
        </div>
      </section>

      {/* Artworks Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {/* 统计信息 */}
          <div className="flex items-center justify-between mb-8">
            <p className="text-amber-600">
              {t('artworks.totalCount', '共 {{count}} 件作品', { count: filteredArtworks.length })}
            </p>
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <Clock className="w-4 h-4" />
              {t('artworks.sortByTime', '按发布时间排序')}
            </div>
          </div>

          {filteredArtworks.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-amber-600 text-lg">{t('artwork.noArtworks', '暂无作品')}</p>
            </div>
          ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
              {filteredArtworks.map((artwork) => (
                <div
                  key={artwork.id}
                  className="break-inside-avoid group cursor-pointer"
                  onClick={() => navigate(`/artwork/${artwork.id}`)}
                  data-testid={`artwork-card-${artwork.id}`}
                >
                  <div className="relative rounded-xl overflow-hidden bg-amber-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
                    {/* 原作出售标识 */}
                    {artwork.is_original_for_sale && artwork.original_price > 0 && (
                      <div className="absolute top-3 left-3 z-20" data-testid={`sale-badge-${artwork.id}`}>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-600 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                          <span className="w-1.5 h-1.5 bg-white rounded-full" />
                          {t('artwork.forSale', 'FOR SALE')}
                        </span>
                      </div>
                    )}
                    {artwork.is_sold && (
                      <div className="absolute top-3 left-3 z-20">
                        <span className="inline-flex items-center px-2.5 py-1 bg-gray-800/80 text-white text-xs font-medium rounded-full">
                          {t('artwork.sold', 'SOLD')}
                        </span>
                      </div>
                    )}
                    {/* 作品图片 */}
                    <img loading="lazy"
                      src={getImageUrl(artwork.image_url)}
                      alt={getLocalizedField(artwork, 'title')}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{ minHeight: '200px' }}
                    />
                    
                    {/* 悬停遮罩 */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                      {/* 操作按钮 */}
                      <div className="absolute top-3 right-3 flex gap-2">
                        <button
                          onClick={(e) => handleLike(e, artwork.id)}
                          className="w-9 h-9 rounded-full bg-amber-50/20 backdrop-blur-md flex items-center justify-center hover:bg-amber-50/40 transition-colors"
                        >
                          <Heart className={`w-4 h-4 ${artwork.is_liked ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                        </button>
                        <button
                          onClick={(e) => handleSave(e, artwork.id)}
                          className="w-9 h-9 rounded-full bg-amber-50/20 backdrop-blur-md flex items-center justify-center hover:bg-amber-50/40 transition-colors"
                        >
                          <Bookmark className={`w-4 h-4 ${artwork.is_saved ? 'fill-white text-white' : 'text-white'}`} />
                        </button>
                      </div>
                      
                      {/* 底部信息 */}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white font-medium mb-1 line-clamp-1">
                          {getLocalizedField(artwork, 'title')}
                        </h3>
                        <div className="flex items-center justify-between text-white/70 text-sm">
                          <span>{artwork.creator_name || artwork.artist}</span>
                          <span className="flex items-center gap-2">
                            <Heart className="w-3 h-3" /> {artwork.likes_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* 卡片底部信息 */}
                  <div className="p-3">
                    <h3 className="font-medium text-amber-900 mb-1 line-clamp-1">
                      {getLocalizedField(artwork, 'title')}
                    </h3>
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/profile/${artwork.creator_id}`);
                        }}
                      >
                        {artwork.creator_avatar ? (
                          <img loading="lazy"
                            src={getImageUrl(artwork.creator_avatar)} 
                            alt="" 
                            className="w-6 h-6 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center">
                            <User className="w-3 h-3 text-amber-600" />
                          </div>
                        )}
                        <span className="text-sm text-amber-700/80 truncate max-w-[100px]">
                          {artwork.creator_name || artwork.artist || t('artwork.anonymousPublisher', '匿名')}
                        </span>
                      </div>
                      <span className="text-xs text-amber-500">
                        {formatDate(artwork.created_at)}
                      </span>
                    </div>
                    {artwork.is_original_for_sale && artwork.original_price > 0 && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-red-600 font-bold text-sm">{artwork.original_price?.toLocaleString()} {t('common.dianUnit', '度电')}</span>
                        <span className="text-xs text-amber-500">≈ ${(artwork.original_price * 0.1)?.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ArtworksPage;
